import { defineStore } from 'pinia';
import { CHANNEL, SUBSCRIPTION } from '@apps-once/definitions';
import { useOnceStore } from '@apps-once/store';
import { useCartStore } from '@apps-once/store/cart';
import { useAddOnStore } from '@apps-once/store/add-on';
import { usePlansStore } from '@apps-billing-shared/store/plans';

export enum CheckoutSteps {
  SELECT_PLAN_DETAILS = 0,
  ADD_CHECKOUT_DETAILS = 1,
  SELECT_ADD_ON_DETAILS = 2,
}

export type SubscriptionPlan = number;

// The most recent initializeCheckout()'s load. Consumers (e.g. the autoPopulate
// AIO gate) await this so they never read business/cart data before it's loaded.
// Defaults resolved so an await before any checkout entry doesn't hang.
let checkoutReady: Promise<void> = Promise.resolve();

// Advances on every checkout entry. A consumer that awaits readiness captures
// this first and re-reads it afterwards: if it moved, a different checkout now
// owns the page and the stale result must be discarded. Callback arrival order
// can't tell them apart, because a new entry reassigns checkoutReady without
// any callback firing (GROWTH-3550).
let checkoutGeneration = 0;

// The resolved default above means an await before any entry is a no-op rather
// than a hang. That is only safe while the classic caller emits
// once:business:checkout before the autoPopulate callback — if that order ever
// changes, the gate would silently stop gating, so say so loudly in dev.
let hasInitializedCheckout = false;

export function whenCheckoutReady(): Promise<void> {
  if (!hasInitializedCheckout && process.env.NODE_ENV !== 'production') {
    console.warn(
      'GROWTH-3550: checkout readiness awaited before initializeCheckout() ran — ' +
        'the gate resolves immediately and is not protecting anything. Check the ' +
        'once:business:checkout / autoPopulate ordering.'
    );
  }
  return checkoutReady;
}

export function currentCheckoutGeneration(): number {
  return checkoutGeneration;
}

type CheckoutStoreState = {
  checkoutStep: number;
  isAioBundle: boolean;
  // True when the checkout was reached from the standalone "Switch to annual"
  // command. A schedule switch keeps the bundle, so the inline retained/lost
  // indicators have nothing useful to say and are suppressed here. Only the
  // Choose Plan modal entry, where the tier really can drop the bundle, shows
  // them.
  suppressBundleRetention: boolean;
  bundleUpsellSeen: boolean;
  // True from an aio_bundle entry until the checkout decides whether to run the
  // bundle checkout. The checkout stays mounted between entries, so without it
  // a re-entry renders the standard checkout until that decision arrives.
  awaitingAioActivation: boolean;
};

export const useCheckoutStore = defineStore('once:checkout', {
  state(): CheckoutStoreState {
    return {
      checkoutStep: CheckoutSteps.SELECT_PLAN_DETAILS,
      isAioBundle: false,
      suppressBundleRetention: false,
      bundleUpsellSeen: false,
      awaitingAioActivation: false,
    };
  },
  getters: {
    currentCheckoutStep(state: CheckoutStoreState): number {
      return state.checkoutStep;
    },
    isSubscriptionStep(state: CheckoutStoreState): boolean {
      return state.checkoutStep === CheckoutSteps.SELECT_PLAN_DETAILS;
    },
    isAioBundleFlow(state: CheckoutStoreState): boolean {
      return state.isAioBundle;
    },
    // Umbrella for every existing-user journey in the bundle checkout: applying
    // the bundle, moving billing schedule, and changing plan at renewal. It
    // answers only "an existing paid subscriber is in the bundle checkout" —
    // trial conversion is not in it — and the journey getters below say which
    // one. What each journey allows differs: changing plan re-picks the tier
    // and offers the upsells, the other two fix both.
    //
    // Inferred from the plan, like isSwitchBillingScheduleFlow — no URL signal.
    isAioExistingUserFlow(): boolean {
      return (
        this.isAioBundleFlow &&
        this.isChangePlanFlow &&
        useOnceStore().getFeatureFlag(
          'wo-3223-enable-aio-bundle-existing-users'
        )
      );
    },
    // The renewal-window Change plan journey (GROWTH-3461): a tenant who already
    // holds the bundle, entering on the plan they hold. The schedule
    // entrypoints send the OTHER schedule's plan, so isAioSwitchBillingFlow
    // separates the two; a tenant without the bundle is applying one, and
    // cannot reach Change plan's redirect at all.
    //
    // Re-picking the tier is the point here, so unlike the other two journeys
    // the base plan stays unlocked.
    isAioBundleChangePlanFlow(): boolean {
      return (
        this.isAioExistingUserFlow &&
        Boolean(useOnceStore().isAioBundle) &&
        !this.isAioSwitchBillingFlow
      );
    },
    // The base plan is fixed: the tenant bundles onto, or keeps, the tier they
    // already hold. Every existing-user journey except Change plan.
    isAioSinglePlanFlow(): boolean {
      return this.isAioExistingUserFlow && !this.isAioBundleChangePlanFlow;
    },
    // The tenant is only adding the bundle: base plan, billing schedule and
    // pre-paid user count all stay as they are. That case has its own endpoint
    // (apply-bundle), which amends the live subscription instead of running a
    // plan change. Changing any of the three falls back to the normal flow.
    // A plan id encodes both the base plan and the billing schedule, so one
    // comparison covers both. The user count needs no comparison: the single-
    // plan view locks the selector (read-only pre-paid on annual, read-only
    // headcount on monthly), so the tenant can't change it here. Comparing it
    // would also couple this getter to annualUserCount, which the cart seeds
    // FROM this getter (setInitialAnnualUserCount) — a dependency best avoided.
    // A tenant already on the bundle is excluded: there is nothing to apply, and
    // their entrypoints (Choose Plan modal, schedule switch) both run a plan
    // change. The eligibility composable keeps them off the apply-bundle CTAs,
    // so this only catches a direct hit on the route.
    isAioBundleApplyOnly(): boolean {
      const onceStore = useOnceStore();
      return (
        this.isAioSinglePlanFlow &&
        !onceStore.isAioBundle &&
        useCartStore().plan === onceStore.plan
      );
    },
    // The apply-bundle journey the Zuora preview prices — an
    // existing annual subscriber (upfront or APM) adding the bundle to the plan
    // they already hold. The existing-user guards (a paid subscriber via
    // isChangePlanFlow, and the wo-3223-enable-aio-bundle-existing-users flag)
    // come from isAioBundleApplyOnly. Monthly is excluded: the backend preview
    // is annual-only.
    isAioApplyBundlePreviewScenario(): boolean {
      return (
        this.isAioBundleApplyOnly &&
        usePlansStore().isAnnual(useOnceStore().plan)
      );
    },
    // A bundle tenant who came in to move billing schedule, as opposed to one
    // applying the bundle to the plan they already hold. Both are single-plan
    // flows, and the difference is the plan the checkout was ENTERED on:
    // initialCartPlan is fixed at entry, so it still tells them apart after the
    // tenant clicks the other schedule — isSwitchBillingScheduleFlow does not,
    // because it reads the live cart plan and turns true in both.
    //
    // Only a tenant who already holds the bundle: the switch-to-annual
    // entrypoint also sends a non-bundle eligible monthly tenant to the bundle
    // URL on an annual plan, and that is a purchase, which keeps both schedules
    // selectable and the "Change plans" heading.
    // Reads the umbrella rather than isAioSinglePlanFlow: that now excludes the
    // change-plan journey, which is itself defined as "not a schedule switch".
    // Behaviourally identical — a schedule switch is never a change-plan entry.
    isAioSwitchBillingFlow(): boolean {
      const onceStore = useOnceStore();
      return (
        this.isAioExistingUserFlow &&
        Boolean(onceStore.isAioBundle) &&
        useCartStore().initialCartPlan !== onceStore.plan
      );
    },
    isCheckoutDetailsStep(state: CheckoutStoreState): boolean {
      return state.checkoutStep === CheckoutSteps.ADD_CHECKOUT_DETAILS;
    },
    isAddOnPurchaseStep(state: CheckoutStoreState): boolean {
      return state.checkoutStep === CheckoutSteps.SELECT_ADD_ON_DETAILS;
    },
    // Promo audience for this checkout. Existing = the tenant already has a paid
    // subscription (adding an add-on, changing plan, or switching billing
    // schedule); everyone else is net-new / trial conversion. The server
    // re-derives audience from isPaid() as the authoritative gate — this only
    // drives client auto-apply and banners.
    promoAudience(): 'new' | 'existing' {
      return this.isAddOnPurchaseStep ||
        this.isChangePlanFlow ||
        this.isSwitchBillingScheduleFlow
        ? 'existing'
        : 'new';
    },
    // AIO-bundle context: the net-new bundle checkout sets isAioBundleFlow; an
    // existing bundle customer is flagged on the once store (svc-dir). Promo
    // codes for the bundle's products (Payroll/HR/base) must not apply/show here.
    isAioBundleCheckout(): boolean {
      const onceStore = useOnceStore();
      return this.isAioBundleFlow || Boolean(onceStore.isAioBundle);
    },
    isSelectingPlanType(state: CheckoutStoreState): boolean {
      return (
        state.checkoutStep === CheckoutSteps.SELECT_PLAN_DETAILS ||
        state.checkoutStep === CheckoutSteps.ADD_CHECKOUT_DETAILS
      );
    },
    isChangePlanFlow(state: CheckoutStoreState): boolean {
      const onceStore = useOnceStore();

      return (
        onceStore.plan !== SUBSCRIPTION.PRO_TRIAL &&
        onceStore.plan !== SUBSCRIPTION.FREE &&
        onceStore.plan !== SUBSCRIPTION.MOBILE_ONLY &&
        onceStore.plan !== SUBSCRIPTION.FREEMIUM &&
        !this.isAppleToDeputyFlow &&
        state.checkoutStep === CheckoutSteps.SELECT_PLAN_DETAILS
      );
    },
    isTrialConvertFlow(): boolean {
      const onceStore = useOnceStore();
      return (
        onceStore.plan === SUBSCRIPTION.PRO_TRIAL ||
        onceStore.plan === SUBSCRIPTION.FREE ||
        onceStore.plan === SUBSCRIPTION.FREEMIUM ||
        this.isAppleToDeputyFlow
      );
    },
    isAppleToDeputyFlow(): boolean {
      const onceStore = useOnceStore();
      return (
        onceStore.channel === CHANNEL.APPSTORE_APPLE &&
        onceStore.getFeatureFlag('wo-2826-apple-to-deputy-self-serve')
      );
    },
    isAnnualToMonthlyAddOnFlow(): boolean {
      const addOnStore = useAddOnStore();
      return addOnStore.status.isMoveFromAnnualToMonthly;
    },
    isMonthlyToAnnualAddOnFlow(): boolean {
      const addOnStore = useAddOnStore();
      return addOnStore.status.isMoveFromMonthlyToAnnual;
    },
    isSwitchBillingScheduleFlow(): boolean {
      if (this.isAddOnPurchaseStep) {
        return false;
      }
      const relatedPlans = usePlansStore().getRelatedPlans(useCartStore().plan);
      return relatedPlans?.includes(useOnceStore().plan) || false;
    },
    isSwitchBillingScheduleToMonthlyFlow(): boolean {
      return (
        this.isSwitchBillingScheduleFlow &&
        usePlansStore().isMonthly(useCartStore().initialCartPlan)
      );
    },
    shouldHideAddOns(state: CheckoutStoreState): boolean {
      const onceStore = useOnceStore();
      return (
        !onceStore.isFreePlan &&
        onceStore.channel !== CHANNEL.APPSTORE_APPLE &&
        onceStore.channel !== CHANNEL.APPSTORE_GOOGLE &&
        state.checkoutStep === CheckoutSteps.SELECT_PLAN_DETAILS
      );
    },
  },
  actions: {
    async initializeCheckout(id: string): Promise<void> {
      checkoutGeneration += 1;
      hasInitializedCheckout = true;
      checkoutReady = (async (): Promise<void> => {
        await useOnceStore().loadBusiness(id);
        useCartStore().setInitialState();
      })();
      return checkoutReady;
    },
    setCheckoutStep(step: CheckoutSteps): void {
      this.checkoutStep = step;
    },
    incrementCheckoutStep(): void {
      if (this.checkoutStep < Object.keys(CheckoutSteps).length / 2 - 1) {
        this.checkoutStep++;
      }
    },
    decrementCheckoutStep(): void {
      if (this.checkoutStep > 0) {
        this.checkoutStep--;
      }
    },
    reset(): void {
      this.checkoutStep = CheckoutSteps.SELECT_PLAN_DETAILS;
      this.isAioBundle = false;
      this.suppressBundleRetention = false;
      this.bundleUpsellSeen = false;
      this.awaitingAioActivation = false;
    },
  },
});
